import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { IndianRupee, Receipt, Fuel } from "lucide-react";

/**
 * Live cost breakdown = Net (rate) + Fuel surcharge (optional) → +GST 18% → Gross.
 * `netAmount` is the picked courier rate (or manual priority rate).
 * `fuelSurcharge` is a controlled number editable by the user.
 */
export default function CostBreakdownPanel({ netAmount = 0, fuelSurcharge = 0, onFuelChange, gstRate = 0.18, testid = "cost-breakdown" }) {
  const net = Number(netAmount) || 0;
  const fuel = Number(fuelSurcharge) || 0;
  const preTax = useMemo(() => +(net + fuel).toFixed(2), [net, fuel]);
  const gst = useMemo(() => +(preTax * gstRate).toFixed(2), [preTax, gstRate]);
  const gross = useMemo(() => +(preTax + gst).toFixed(2), [preTax, gst]);

  return (
    <div className="border border-border rounded-md bg-card p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Receipt size={14} className="text-primary" />
        <div className="overline">Cost breakdown</div>
      </div>

      <Row label="Net amount (courier rate)" value={net} testid={`${testid}-net`} icon={IndianRupee} />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Fuel size={12} className="text-muted-foreground" />
          <span>Fuel surcharge <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Optional</span></span>
        </div>
        <div className="flex items-center gap-1 font-mono text-sm">
          <span className="text-muted-foreground">₹</span>
          <Input
            data-testid={`${testid}-fuel`}
            type="number"
            step="0.01"
            min="0"
            value={fuelSurcharge ?? ""}
            onChange={(e) => onFuelChange && onFuelChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
            className="w-24 rounded-md border-input focus-visible:ring-primary font-mono text-right"
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="border-t border-dashed border-border pt-2 space-y-2">
        <Row label="Pre-tax total" value={preTax} muted testid={`${testid}-pretax`} />
        <Row label={`GST @ ${Math.round(gstRate * 100)}%`} value={gst} muted testid={`${testid}-gst`} />
      </div>

      <div className="border-t border-border pt-3 flex items-end justify-between">
        <div>
          <div className="overline text-primary">Gross amount</div>
          <div className="text-[10px] text-muted-foreground">Payable · incl. GST</div>
        </div>
        <div className="font-display text-3xl font-mono text-primary" data-testid={`${testid}-gross`}>₹{gross.toFixed(2)}</div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className="font-mono">₹{Number(value || 0).toFixed(2)}</span>
    </div>
  );
}
